import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCityBySlug } from '../../config/cities';
import StepperForm from '../../components/feature/StepperForm';
import EducationPanel from '../../components/feature/EducationPanel';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track, TRACKING_EVENTS } from '../../utils/tracking';
import { checkQualification } from '../../utils/qualification';

interface FormAnswers {
  timeInBusiness?: string;
  monthlyRevenue?: string;
  industry?: string;
  fundingPurpose?: string;
  fundingAmount?: string;
  goal?: string;
  creditScore?: string;
}

export default function CityPrequalificationPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  
  // Get city from the URL parameter
  const city = citySlug ? getCityBySlug(citySlug) : null;

  const [showSummary, setShowSummary] = useState(false);
  const [formAnswers, setFormAnswers] = useState<FormAnswers>({});
  const [isFormComplete, setIsFormComplete] = useState(false);

  useEffect(() => {
    if (!city) {
      navigate('/business-funding');
      return;
    }

    track(TRACKING_EVENTS.CITY_SELECTED, {
      city: city.slug,
      city_name: city.name,
      page_type: 'prequalification'
    });
  }, [city, navigate]);

  const scrollToForm = () => {
    const formElement = document.getElementById('prequalification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFormUpdate = (answers: FormAnswers) => {
    setFormAnswers(answers);
  };

  const handleFormComplete = (answers: FormAnswers) => {
    setFormAnswers(answers);
    setIsFormComplete(true);
    track(TRACKING_EVENTS.FORM_COMPLETE, {
      city: city?.slug,
      ...answers
    });
  };

  const getQualifiedFundingTypes = () => {
    const qualification = checkQualification(formAnswers);
    if (!qualification.isQualified) return [];

    const types: string[] = [];
    const goal = formAnswers.goal;
    const fundingPurpose = formAnswers.fundingPurpose;

    if (goal === 'working-capital' || fundingPurpose === 'working-capital') {
      types.push('Working Capital Loans');
    }
    if (goal === 'line-of-credit') {
      types.push('Business Line of Credit');
    }
    if (goal === 'equipment' || fundingPurpose === 'equipment') {
      types.push('Equipment Financing');
    }
    if (goal === 'term-loan') {
      types.push('Term Loans');
    }
    if (goal === 'expansion' || fundingPurpose === 'expansion') {
      types.push('Term Loans for Expansion');
    }
    if (goal === 'sba-cre' || fundingPurpose === 'real-estate') {
      types.push('SBA Loans', 'Commercial Real Estate Financing');
    }
    if (fundingPurpose === 'inventory') {
      types.push('Inventory Financing');
    }
    if (fundingPurpose === 'debt-consolidation') {
      types.push('Debt Consolidation Loans');
    }

    // Default options if none specified
    if (types.length === 0) {
      types.push('Working Capital Loans', 'Business Line of Credit', 'Term Loans');
    }

    return types;
  };

  if (!city) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Serving {city.name} Businesses</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Business Funding Options for <span className="text-red-600">{city.name}</span> Businesses
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Get pre-qualified in minutes for business financing tailored to {city.name} companies. 
              Compare options from multiple lenders with no impact to your credit score.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={scrollToForm}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Start Pre-Qualification
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a
                href={`/business-funding/${city.slug}`}
                className="text-gray-700 hover:text-red-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors border-2 border-gray-300 hover:border-red-600 whitespace-nowrap cursor-pointer"
              >
                Learn More
              </a>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              <i className="ri-shield-check-line text-green-600 mr-1"></i>
              Educational platform • Not a lender • No guarantee of approval
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, fast, and transparent process to explore your funding options
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-3xl text-red-600"></i>
              </div>
              <div className="bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Answer Questions
              </h3>
              <p className="text-gray-600">
                Complete our quick pre-qualification form with basic information about your {city.name} business, revenue, and funding needs.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-3xl text-red-600"></i>
              </div>
              <div className="bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Review Options
              </h3>
              <p className="text-gray-600">
                See which funding types you may qualify for based on your business profile. Get educational insights about each option.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-red-600"></i>
              </div>
              <div className="bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Continue to Application
              </h3>
              <p className="text-gray-600">
                If pre-qualified, proceed to our secure partner application to complete your funding request and receive offers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Is This Right for Your Business?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our platform works best for established {city.name} businesses seeking growth capital
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Who This Is For */}
            <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-green-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Great Fit If You Have:
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">At least 12 months in business</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">$30,000+ in monthly revenue</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Seeking $50,000 or more in funding</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Clear business purpose for funds</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Operating in {city.name}, {city.stateCode}</span>
                </li>
              </ul>
            </div>

            {/* Who This Is Not For */}
            <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-red-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="ri-close-circle-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Not Ideal If You Have:
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-xl text-red-600 mt-1"></i>
                  <span className="text-gray-700">Less than 12 months in business</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-xl text-red-600 mt-1"></i>
                  <span className="text-gray-700">Under $30,000 monthly revenue</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-xl text-red-600 mt-1"></i>
                  <span className="text-gray-700">Seeking less than $50,000</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-xl text-red-600 mt-1"></i>
                  <span className="text-gray-700">Personal loans or consumer credit needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-xl text-red-600 mt-1"></i>
                  <span className="text-gray-700">Looking for guaranteed approval</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Qualification Form Section */}
      <section id="prequalification-form" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!showSummary ? (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Pre-Qualify for {city.name} Business Funding
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Answer a few quick questions to see which funding options may be available for your business
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Form */}
                <div className="order-2 lg:order-1">
                  <StepperForm 
                    onUpdate={handleFormUpdate}
                    onComplete={handleFormComplete}
                    cityContext={city.slug}
                  />
                </div>

                {/* Education Panel */}
                <div className="order-1 lg:order-2">
                  <div className="lg:sticky lg:top-24">
                    <EducationPanel 
                      answers={formAnswers}
                      showReadyToApply={isFormComplete}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <SummarySection 
              answers={formAnswers} 
              city={city} 
              qualifiedTypes={getQualifiedFundingTypes()}
            />
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface SummarySectionProps {
  answers: FormAnswers;
  city: any;
  qualifiedTypes: string[];
}

function SummarySection({ answers, city, qualifiedTypes }: SummarySectionProps) {
  const qualification = checkQualification(answers);

  const handleContinue = () => {
    track(TRACKING_EVENTS.REDIRECT_CLICK, {
      city: city.slug,
      qualified: qualification.isQualified,
      ...answers
    });

    // Redirect will be handled by StepperForm's existing logic
    const continueButton = document.querySelector('[data-continue-button]') as HTMLButtonElement;
    if (continueButton) {
      continueButton.click();
    }
  };

  const handleEditAnswers = () => {
    window.location.reload();
  };

  return (
    <div id="summary-section" className="max-w-4xl mx-auto scroll-mt-20">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-file-list-check-line text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold">Review Your Responses</h2>
          </div>
          <p className="text-red-100">
            Based on your {city.name} business profile
          </p>
        </div>

        {/* Summary Content */}
        <div className="p-8">
          {/* Answers Summary */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Your Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {answers.timeInBusiness && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Time in Business</div>
                  <div className="font-semibold text-gray-900">{answers.timeInBusiness}</div>
                </div>
              )}
              {answers.monthlyRevenue && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Monthly Revenue</div>
                  <div className="font-semibold text-gray-900">${answers.monthlyRevenue}</div>
                </div>
              )}
              {answers.industry && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Industry</div>
                  <div className="font-semibold text-gray-900">{answers.industry}</div>
                </div>
              )}
              {answers.fundingPurpose && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Funding Purpose</div>
                  <div className="font-semibold text-gray-900">{answers.fundingPurpose}</div>
                </div>
              )}
              {answers.fundingAmount && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Funding Amount</div>
                  <div className="font-semibold text-gray-900">${answers.fundingAmount}</div>
                </div>
              )}
              {answers.goal && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Product Type</div>
                  <div className="font-semibold text-gray-900">{answers.goal}</div>
                </div>
              )}
              {answers.creditScore && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Credit Score Range</div>
                  <div className="font-semibold text-gray-900">{answers.creditScore}</div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Location</div>
                <div className="font-semibold text-gray-900">{city.name}, {city.stateCode}</div>
              </div>
            </div>
          </div>

          {/* Qualification Result */}
          {qualification.isQualified ? (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    You May Qualify!
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Based on your responses, you appear to meet initial requirements for business funding. 
                    You may qualify for the following options:
                  </p>
                  <ul className="space-y-2">
                    {qualifiedTypes.map((type, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-800">
                        <i className="ri-arrow-right-s-line text-green-600"></i>
                        <span className="font-medium">{type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-red-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Currently Not Eligible
                  </h3>
                  <p className="text-gray-700 mb-2">
                    {qualification.disqualificationReason}
                  </p>
                  <p className="text-gray-600 text-sm">
                    We recommend building your business profile and returning when you meet the minimum requirements.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            {qualification.isQualified ? (
              <>
                <button
                  onClick={handleContinue}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Continue to Secure Pre-Application
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                <button
                  onClick={handleEditAnswers}
                  className="sm:w-auto text-gray-700 hover:text-red-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors border-2 border-gray-300 hover:border-red-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Edit Answers
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEditAnswers}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Edit Answers
                </button>
                <button
                  onClick={handleEditAnswers}
                  className="sm:w-auto text-gray-700 hover:text-red-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors border-2 border-gray-300 hover:border-red-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-restart-line mr-2"></i>
                  Start Over
                </button>
              </>
            )}
          </div>

          {/* Disclaimer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              <i className="ri-information-line mr-1"></i>
              Pre-qualification does not guarantee approval. Final eligibility subject to lender review and verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
